import { useState, type FormEvent } from "react";
import { authFehler, authFehlerArt } from "../../konto/auth";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lockedStaffOrgs, useAccount, useStaffWorkspace, useSwitchStaffOrg, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { browserTimeZone } from "../../../lib/zeitzone";
import { ErrorState, Loading } from "../../../components/States";
import { PageHead, useAction } from "../../../components/ui";
import { ZweiFaktorVerwalten } from "../../../components/ZweiFaktor";
import { talentName } from "../../../data/queries";
import { loadProfil, requestAccountDeletion, saveMemberSelf, savePrefs, type ProfilData } from "./data";
import { Check, RIGHTS, SICHERHEIT_PFAD, UI_LANGUAGES, suggestInitials, validateProfil, type ProfilDraft } from "./model";
import { CheckField, Confirm, DEMO_NOTE, EinstellungenRahmen, FormErrors, RIGHTS_NOTE, SelectField, TextField } from "./parts";
import { SignaturePad, type SignatureValue } from "../vertraege/Unterschrift";
import { StudioSupportZugaenge } from "./Team";
import { CaptchaFeld, captchaConfig } from "../../konto/Captcha";
import { loadSavedSignature, saveSignatureFor, type SavedSignature } from "../vertraege/gespeicherte-unterschrift";
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, passwordHint } from "@talentkontor/domain";
import { t, ta, currentLang } from "../../../lib/sprache";
import { AppInstallieren } from "../../../components/AppInstallieren";
import { DatumsformatWahl } from "../../../components/Datumsformat";
import { DarstellungWahl } from "../../../components/Darstellung";
import { today as todayIn } from "../../../lib/format";

/**
 * Mein Profil (/agentur/profil, K-372): Angaben (Anzeigename, Funktion, Kürzel), Sprache, Darstellung (Zeitzone, Datumsformat,
 * Hell/Dunkel), eigene Rechte, Unterschrift, App und Konto löschen. Passwort, Anmeldeadresse, zweiter Faktor und Sitzungen
 * stehen seit K-372 unter „Anmeldung & Sicherheit“ (/agentur/einstellungen/sicherheit). Für Admins und Manager.
 */
export function Profil() {
  const ws = useStaffWorkspace();
  const userId = api.session()?.userId ?? "";
  const { account } = useAccount();
  const q = useQuery({ queryKey: ["user_preferences", userId, ws.org.org_id], queryFn: () => loadProfil(api, userId, ws.org.org_id) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const d = q.data;
  return (
    <EinstellungenRahmen current="profil">
      <PageHead eyebrow={t("Persönlich", "Personal")} title={ws.isCreator ? t("Mein Zugang", "My access") : t("Mein Profil", "My profile")}
        text={<>{t("Passwort, Anmeldeadresse, zweiter Faktor und Sitzungen: ", "Password, sign-in address, second factor and sessions: ")}<Link to={SICHERHEIT_PFAD}>{t("Anmeldung & Sicherheit", "Sign-in & security")}</Link></>} />
      <ProfilForm key={`${d.membership?.updated_at ?? ""}|${d.prefs?.updated_at ?? ""}`} ws={ws} data={d} />
      {/* D-1: Zeitzone und Datumsformat nebeneinander (je ein Feld) */}
      <div className="grid tk-oben">
        <Zeitzone orgZone={ws.org.timezone} />
        <DatumsformatWahl userId={userId} value={account?.dateFormat} today={todayIn(ws.org.timezone)} heading="h3" />
      </div>
      {/* IA-1: Anker „Design“ aus dem Profilmenü */}
      {userId && <div id="darstellung"><DarstellungWahl userId={userId} heading="h3" /></div>}
      {/* 0330: nur Plattform-Admins */}
      {/* Studio (Creator-Konto): eine Person, keine Rollen und Rechte */}
      {!ws.isCreator && <Moeglichkeiten ws={ws} data={d} />}
      {(ws.isChef || ws.can("sign_contracts")) && <GespeicherteUnterschrift fallbackName={d.membership?.display_name ?? ws.name} />}
      <AppInstallieren heading="h3" />
      <KontoLoeschen creator={!!ws.isCreator} />
      {api.mode === "demo" && <p className="demo-note">{DEMO_NOTE()}</p>}
    </EinstellungenRahmen>
  );
}

/**
 * Anmeldung & Sicherheit (/agentur/einstellungen/sicherheit, K-372): Passwort und Anmeldeadresse ändern, zweiter Faktor
 * (derselbe Baustein wie /konto/sicherheit) und Sitzungen (Abmelden, auf allen Geräten abmelden, Agentur wechseln).
 * Für jede Person im Team.
 */
export function AnmeldungSicherheit() {
  const ws = useStaffWorkspace();
  return (
    <EinstellungenRahmen current="sicherheit">
      <PageHead eyebrow={t("Persönlich", "Personal")} title={t("Anmeldung & Sicherheit", "Sign-in & security")}
        text={t("Passwort, Anmeldeadresse, zweiter Faktor und deine Sitzungen.", "Password, sign-in address, second factor and your sessions.")} />
      <Anmeldung />
      <ZweiFaktorVerwalten headingId="zwei-faktor-title" />
      {/* IA-1: Vorgabe fürs ganze Team steht unter Alle Einstellungen · Team & Rollen (keine Doppelung des Schalters) */}
      {ws.isChef && !ws.isCreator && (
        <p className="notice">{t("Den zweiten Faktor für das ganze Team schreibst du unter ", "You require the second factor for the whole team under ")}
          <Link to="/agentur/einstellungen/team#sicherheit">{t("Alle Einstellungen · Sicherheit fürs Team", "All settings · Team security")}</Link> {t("vor.", ".")}</p>
      )}
      {/* Studio (Creator-Konto): Support-Zugänge stehen hier statt unter Team & Rollen */}
      {ws.isChef && ws.isCreator && <div id="sicherheit"><StudioSupportZugaenge orgId={ws.org.org_id} timeZone={ws.org.timezone} /></div>}
      <Sitzungen ws={ws} />
      {api.mode === "demo" && <p className="demo-note">{DEMO_NOTE()}</p>}
    </EinstellungenRahmen>
  );
}

function ProfilForm({ ws, data }: { ws: StaffWorkspace; data: ProfilData }) {
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const email = api.session()?.email ?? "";
  const displayName = data.membership?.display_name ?? "";
  const [d, setD] = useState<ProfilDraft>({
    display_name: displayName,
    job_title: data.membership?.job_title ?? "",
    initials: data.prefs?.initials ?? "",
    language: data.prefs?.language ?? "",
    desktop_notifications: data.prefs?.desktop_notifications ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof ProfilDraft>(k: K) => (v: ProfilDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  // Anzeigename und Funktion: jede Person für sich (membership_update_self), der Admin auch unter Team & Rechte
  const canEdit = !!data.membership;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = validateProfil(d, { canEditMembership: canEdit });
    setErrors(r.errors);
    if (!r.values) return;
    const v = r.values;
    void run("profil", async () => {
      if (v.membership && data.membership && v.membership.display_name
          && (v.membership.display_name !== data.membership.display_name || v.membership.job_title !== data.membership.job_title)) {
        await saveMemberSelf(api, ws.org.org_id, v.membership.display_name, v.membership.job_title);
      }
      await savePrefs(api, api.session()!.userId, v.prefs);
      // Name und Kürzel stehen in der Kopfzeile (Kontodaten)
      await qc.invalidateQueries({ queryKey: ["account"] });
    }, t("Profil gespeichert.", "Profile saved."));
  }

  return (
    <form className="panel stack" onSubmit={submit} noValidate aria-busy={!!busy}>
      <h2>{t("Persönliche Angaben", "Personal details")}</h2>
      <div className="grid">
        <TextField label={t("Anzeigename", "Display name")} value={d.display_name} onChange={set("display_name")} error={errors.display_name} disabled={!canEdit} autoComplete="name" />
        <TextField label={t("Funktion", "Job title")} value={d.job_title} onChange={set("job_title")} error={errors.job_title} disabled={!canEdit} autoComplete="organization-title" />
      </div>
      <small>{ta("So erscheinst du im Team, im Chat mit Creatorn und im Protokoll.", "This is how you appear in the team, in chats with creators and in the log.", "So erscheinst du im Protokoll.", "This is how you appear in the log.")}</small>
      <TextField label={t("E-Mail", "Email")} type="email" value={email} onChange={() => undefined} readOnly hint={t("Mit dieser Adresse meldest du dich an.", "You sign in with this address.")} />
      <TextField label={t("Kürzel", "Initials")} value={d.initials} onChange={(v) => set("initials")(v.toUpperCase())} error={errors.initials} maxLength={4}
        placeholder={suggestInitials(displayName || email)} hint={t("Erscheint im runden Zeichen oben rechts. Leer lassen: aus dem Namen.", "Appears in the round badge at the top right. Leave empty: from the name.")} />
      <h2 id="sprache">{t("Sprache und Hinweise", "Language and notices")}</h2>
      <SelectField label={t("Sprache", "Language")} value={d.language} onChange={set("language")} options={UI_LANGUAGES}
        placeholder={ta(`Wie die Agentur (${ws.org.default_document_language === "en" ? "English" : "Deutsch"})`, `Same as the agency (${ws.org.default_document_language === "en" ? "English" : "German"})`,
          `Wie dein Konto (${ws.org.default_document_language === "en" ? "English" : "Deutsch"})`, `Same as your account (${ws.org.default_document_language === "en" ? "English" : "German"})`)}
        hint={ta("Gilt für die Oberfläche und für Mails an dich (Aufgaben, Nachrichten). Belege und Mails an Marken und Creator richten sich nach deren Sprache.", "Applies to the interface and to emails to you (tasks, messages). Documents and emails to brands and creators follow their language.",
          "Gilt für die Oberfläche und für Mails an dich (Aufgaben, Erinnerungen). Belege und Mails an Marken richten sich nach deren Sprache.", "Applies to the interface and to emails to you (tasks, reminders). Documents and emails to brands follow their language.")} />
      <CheckField label={t("Benachrichtigungen auf dem Desktop", "Desktop notifications")} checked={d.desktop_notifications} onChange={set("desktop_notifications")} />
      <FormErrors errors={errors} error={error} />
      <button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Profil speichern", "Save profile")}</button>
    </form>
  );
}

/**
 * Eigene Zeitzone (Befund 12): Kalender, Chat und Glocke zeigen Uhrzeiten in dieser Zone; ohne Wahl gilt die des Browsers.
 * Eigene Zeile in user_preferences (RLS: nur die eigene), getrennt vom übrigen Profil gespeichert.
 */
/**
 * Gespeicherte Unterschrift fürs Gegenzeichnen (PARITAET-12): zeichnen oder eingescannt hochladen; füllt das Feld beim
 * Gegenzeichnen vor. Nur für die Person selbst sichtbar (user_preferences, 0080).
 */
function GespeicherteUnterschrift({ fallbackName }: { fallbackName: string }) {
  const userId = api.session()?.userId ?? "";
  const q = useQuery({ queryKey: ["user_preferences", userId, "unterschrift"], queryFn: () => loadSavedSignature(api, userId), enabled: !!userId });
  if (q.isPending) return null;
  return <UnterschriftForm key={q.data?.image ?? "leer"} userId={userId} saved={q.data ?? null} fallbackName={fallbackName} />;
}
function UnterschriftForm({ userId, saved, fallbackName }: { userId: string; saved: SavedSignature | null; fallbackName: string }) {
  const qc = useQueryClient();
  const { busy, run } = useAction();
  const [sig, setSig] = useState<SignatureValue>({ name: saved?.name ?? fallbackName, dataUrl: saved?.image ?? null });
  const done = () => qc.invalidateQueries({ queryKey: ["user_preferences", userId, "unterschrift"] });
  return (
    <section className="panel stack" aria-labelledby="unterschrift-title">
      <h3 id="unterschrift-title">{t("Gespeicherte Unterschrift", "Saved signature")}</h3>
      <p>{t("Füllt beim Gegenzeichnen von Verträgen dein Unterschriftsfeld vor. Zeichne sie oder lade eine eingescannte Unterschrift hoch. Nur du siehst sie.", "Pre-fills your signature field when countersigning contracts. Draw it or upload a scanned signature. Only you can see it.")}</p>
      <SignaturePad value={sig} onChange={setSig} nameLabel={t("Vollständiger Name", "Full name")} upload />
      <div className="row">
        <button type="button" className="primary" disabled={!!busy || !sig.dataUrl || sig.name.trim().length < 2}
          onClick={() => void run("unterschrift", async () => { await saveSignatureFor(api, userId, sig); await done(); }, t("Unterschrift gespeichert.", "Signature saved."))}>
          {busy === "unterschrift" ? t("Wird gespeichert …", "Saving …") : t("Unterschrift speichern", "Save signature")}
        </button>
        {saved?.image && <button type="button" disabled={!!busy} onClick={() => void run("unterschrift-weg", async () => { await saveSignatureFor(api, userId, null); await done(); }, t("Unterschrift entfernt.", "Signature removed."))}>{t("Entfernen", "Remove")}</button>}
      </div>
      {!sig.dataUrl && <small>{t("Mit getipptem Namen gibt es nichts zu speichern; wähle „Unterschrift zeichnen“ oder „Bild verwenden“.", "With a typed name there is nothing to save; choose “Draw signature” or “Use image”.")}</small>}
    </section>
  );
}

function Zeitzone({ orgZone }: { orgZone: string }) {
  const userId = api.session()?.userId ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["user_preferences", userId, "zeitzone"], queryFn: async () => {
    const rows = await api.select<{ time_zone: string | null }>("user_preferences", { columns: "time_zone", filters: [["user_id", "eq", userId]] }).catch(() => []);
    return rows[0]?.time_zone ?? null;
  } });
  const { busy, run } = useAction();
  const browser = browserTimeZone();
  const zonen = [...new Set([q.data, browser, orgZone, "Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/London", "Europe/Lisbon", "Europe/Madrid",
    "America/New_York", "America/Los_Angeles", "UTC"].filter((z): z is string => !!z))];
  const speichern = (zone: string) => void run("zeitzone", async () => {
    const value = zone || null;
    const rows = await api.update<{ user_id: string }>("user_preferences", [["user_id", "eq", userId]], { time_zone: value, updated_at: new Date().toISOString() }, { columns: "user_id" });
    if (!rows.length) await api.insert("user_preferences", { user_id: userId, time_zone: value }, { columns: "user_id" });
    await qc.invalidateQueries({ queryKey: ["user_preferences", userId, "zeitzone"] });
  }, t("Zeitzone gespeichert.", "Time zone saved."));
  return (
    <section className="panel stack" aria-labelledby="zeitzone-title">
      <h3 id="zeitzone-title">{t("Zeitzone", "Time zone")}</h3>
      <SelectField label={t("Uhrzeiten anzeigen in", "Show times in")} value={q.data ?? ""} onChange={speichern} disabled={!!busy || q.isPending}
        options={[{ key: "", label: t(`Zeitzone des Browsers${browser ? ` (${browser})` : ""}`, `Browser time zone${browser ? ` (${browser})` : ""}`) }, ...zonen.map((z) => ({ key: z, label: z }))]}
        hint={ta(`Kalender, Nachrichten und Benachrichtigungen rechnen in diese Zone. Die Agentur arbeitet in ${orgZone}.`, `Calendar, messages and notifications use this zone. The agency works in ${orgZone}.`,
          `Kalender und Benachrichtigungen rechnen in diese Zone. Dein Konto arbeitet in ${orgZone}.`, `Calendar and notifications use this zone. Your account works in ${orgZone}.`)} />
    </section>
  );
}

function Moeglichkeiten({ ws, data }: { ws: StaffWorkspace; data: ProfilData }) {
  const m = data.membership;
  const scope = ws.isChef || m?.sees_all_talents
    ? t("alle Creator", "all creators")
    : data.assigned.length ? t(`nur zugewiesene Creator: ${data.assigned.map((t) => talentName(t)).join(", ")}`, `assigned creators only: ${data.assigned.map((t) => talentName(t)).join(", ")}`) : t("nur zugewiesene Creator (derzeit keine)", "assigned creators only (currently none)");
  const perm: Record<string, "approve_invoices" | "send_invoices" | "dun" | "confirm_payments" | "trigger_payouts" | "sign_contracts" | "edit_templates" | "edit_contract_text" | "view_financials" | "view_amounts"> = {
    can_approve_invoices: "approve_invoices", can_send_invoices: "send_invoices", can_dun: "dun",
    can_confirm_payments: "confirm_payments", can_trigger_payouts: "trigger_payouts", can_sign_contracts: "sign_contracts",
    can_edit_templates: "edit_templates", can_edit_contract_text: "edit_contract_text", can_view_financials: "view_financials", can_view_amounts: "view_amounts",
  };
  const missing = RIGHTS.filter((r) => !ws.can(perm[r.key]!));
  return (
    <section className="panel stack" aria-labelledby="moeglichkeiten-title">
      <h3 id="moeglichkeiten-title">{t("Deine Möglichkeiten", "Your options")}</h3>
      <div className="notice">{ws.isChef ? t("Admin", "Admin") : "Manager"} · {ws.name} · {scope}</div>
      <div className="row">
        {RIGHTS.map((r) => ws.can(perm[r.key]!)
          ? <span key={r.key} className="pill success"><span className="pill-zeichen">✓</span> {r.label}</span>
          : <span key={r.key} className="pill"><span className="pill-zeichen">○</span> {r.label}</span>)}
        {ws.isChef && <span className="pill success"><span className="pill-zeichen">✓</span> {t("Einstellungen, Team und Abo", "Settings, team and subscription")}</span>}
      </div>
      {missing.length > 0 && <p id="rights-note">{RIGHTS_NOTE().replace("Aktion ist", missing.length === 1 ? "Aktion ist" : "Aktionen sind")}</p>}
    </section>
  );
}

function Anmeldung() {
  return (
    <section className="panel stack" aria-labelledby="anmeldung-title">
      <h3 id="anmeldung-title">{t("Anmeldung", "Sign-in")}</h3>
      <PasswortAendern />
      <AdresseAendern />
      <div className="row">
        <Link className="btn" to="/passwort">{t("Passwort vergessen", "Forgot password")}</Link>
      </div>
    </section>
  );
}

/** Sitzungen (K-372): Abmelden auf diesem bzw. allen Geräten, Agentur wechseln, gesperrte Agenturen, eigene Agentur anlegen. */
function Sitzungen({ ws }: { ws: StaffWorkspace }) {
  const navigate = useNavigate();
  const { account } = useAccount();
  const switchOrg = useSwitchStaffOrg();
  const [busy, setBusy] = useState(false);
  // Wählbar sind Agenturen mit lesbarem Profil; gesperrte oder gekündigte stehen darunter als Hinweis (RECHTE-4)
  const orgs = (account?.memberships ?? []).filter((m) => account?.orgs[m.org_id]).map((m) => ({ key: m.org_id, label: account!.orgs[m.org_id]!.name }));
  const locked = account ? lockedStaffOrgs(account).length : 0;
  const [busyAll, setBusyAll] = useState(false);
  // Nur dieses Gerät (SI-05-N); „überall“ beendet auch Handy, Laptop und installierte App
  async function signOut(everywhere = false) {
    (everywhere ? setBusyAll : setBusy)(true);
    try {
      await api.signOut(everywhere ? { everywhere: true } : undefined);
      navigate("/anmelden", { replace: true });
    } finally {
      (everywhere ? setBusyAll : setBusy)(false);
    }
  }
  return (
    <section className="panel stack" aria-labelledby="sitzungen-title">
      <h3 id="sitzungen-title">{t("Sitzungen", "Sessions")}</h3>
      <div className="row">
        <button className="" type="button" onClick={() => void signOut()} disabled={busy || busyAll} aria-busy={busy}>{busy ? t("Wird abgemeldet …", "Signing out …") : t("Abmelden", "Sign out")}</button>
        <button className="" type="button" onClick={() => void signOut(true)} disabled={busy || busyAll} aria-busy={busyAll}>{busyAll ? t("Wird abgemeldet …", "Signing out …") : t("Auf allen Geräten abmelden", "Sign out on all devices")}</button>
      </div>
      <small>{t("„Abmelden“ beendet nur die Sitzung auf diesem Gerät; „Auf allen Geräten abmelden“ auch die auf Handy, Laptop und in der installierten App.", "“Sign out” only ends the session on this device; “Sign out on all devices” also ends those on phone, laptop and in the installed app.")}</small>
      {orgs.length > 1 && <AgenturWechsel value={ws.org.org_id} orgs={orgs} onChange={(v) => switchOrg(v)} />}
      {locked > 0 && (
        <small>
          {locked === 1 ? t("Eine weitere Agentur ist", "One more agency is") : t(`${locked} weitere Agenturen sind`, `${locked} more agencies are`)} {t("gerade gesperrt oder beendet und nur für den Admin erreichbar; sie erscheinen wieder, sobald sie freigeschaltet sind.", "currently suspended or ended and only accessible to the admin; they reappear as soon as they are reactivated.")}</small>
      )}
      {!ws.isChef && <small>{t("Eine eigene Agentur legst du über die ", "You create your own agency via ")}<Link to="/einrichtung/firma">{t("Einrichtung", "Setup")}</Link> {t("an.", ".")}</small>}
    </section>
  );
}

/** Passwort ändern mit dem bisherigen Passwort (erneute Anmeldung), nicht nur über den Link aus der Mail. */
function PasswortAendern() {
  const { busy, error, run } = useAction();
  const [old, setOld] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const demo = api.mode === "demo";
  // Die erneute Anmeldung mit dem bisherigen Passwort braucht bei eingeschaltetem CAPTCHA ein Token (Befund 02.10.2026)
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = !demo && !!captchaConfig();
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const c = new Check();
    c.text("old", old, { label: t("dein bisheriges Passwort", "your current password"), required: true });
    c.text("next", next, { label: t("ein neues Passwort", "a new password"), required: true, min: MIN_PASSWORD_LENGTH, max: MAX_PASSWORD_LENGTH });
    if (next && again !== next) c.fail("again", t("Die Wiederholung passt nicht zum neuen Passwort.", "The repetition does not match the new password."));
    if (next && old && next === old) c.fail("next", t("Das neue Passwort ist dasselbe wie das bisherige.", "The new password is the same as the previous one."));
    if (mitCaptcha && !captcha) c.fail("captcha", t("Bitte die Sicherheitsprüfung lösen.", "Please complete the security check."));
    setErrors(c.errors);
    if (!c.ok) return;
    const email = api.session()?.email ?? "";
    void run("passwort", async () => {
      try {
        await api.signInWithPassword(email, old, { captchaToken: captcha });
      } catch (e) {
        // Praxistest K-221: nur falsche Zugangsdaten heißen „bisheriges Passwort stimmt nicht“; Sicherheitsprüfung, Limit usw. im Klartext
        if (authFehlerArt(e) === "invalid_credentials") throw new Error(t("Das bisherige Passwort stimmt nicht.", "The previous password is incorrect."));
        throw new Error(authFehler(e, currentLang()));
      }
      await api.updatePassword(next);
    }, t("Passwort geändert. Andere Geräte sind abgemeldet.", "Password changed. Other devices have been signed out.")).then((ok) => {
      if (mitCaptcha) setCaptchaRunde((n) => n + 1);
      if (!ok) return;
      setOld("");
      setNext("");
      setAgain("");
    });
  }
  if (demo) return <p>{t("In der Demo gibt es keine Passwörter; Passwort und Anmeldeadresse ändern geht in der echten Anwendung hier.", "There are no passwords in the demo; changing the password and sign-in address works here in the real application.")}</p>;
  return (
    <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "passwort"}>
      <strong>{t("Passwort ändern", "Change password")}</strong>
      <div className="grid three">
        <TextField label={t("Bisheriges Passwort", "Current password")} type="password" value={old} onChange={setOld} error={errors.old} autoComplete="current-password" />
        <TextField label={t("Neues Passwort", "New password")} type="password" value={next} onChange={setNext} error={errors.next} autoComplete="new-password" hint={passwordHint(currentLang())} />
        <TextField label={t("Neues Passwort wiederholen", "Repeat new password")} type="password" value={again} onChange={setAgain} error={errors.again} autoComplete="new-password" />
      </div>
      {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
      {errors.captcha && <small className="tk-error" role="alert">{errors.captcha}</small>}
      <FormErrors errors={{}} error={busy ? null : error} />
      <div className="row"><button type="submit" disabled={!!busy}>{busy === "passwort" ? t("Wird geändert …", "Changing …") : t("Passwort ändern", "Change password")}</button></div>
    </form>
  );
}

/** Anmeldeadresse ändern: gilt erst nach dem Bestätigungslink an die neue Adresse (Demo: sofort). */
function AdresseAendern() {
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [sent, setSent] = useState<string | null>(null);
  if (!api.updateEmail || api.mode === "demo") return null;
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const c = new Check();
    const v = c.email("email", email, { label: t("die neue Adresse", "the new address"), required: true });
    if (v && v === api.session()?.email) c.fail("email", t("Das ist schon deine Anmeldeadresse.", "This is already your sign-in address."));
    setFieldError(c.errors.email);
    if (!c.ok || !v) return;
    void run("adresse", async () => {
      const r = await api.updateEmail!(v);
      setSent(r.needsConfirmation ? v : null);
      await qc.invalidateQueries({ queryKey: ["account"] });
    }, t("Adresse geändert.", "Address changed.")).then((ok) => ok && setEmail(""));
  }
  return (
    <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "adresse"}>
      <strong>{t("Anmeldeadresse ändern", "Change sign-in address")}</strong>
      <TextField label={t("Neue E-Mail-Adresse", "New email address")} type="email" value={email} onChange={setEmail} error={fieldError} autoComplete="email" />
      {sent && <div className="notice" role="status">{t("Wir haben einen Bestätigungslink an ", "We have sent a confirmation link to ")}{sent} {t("geschickt. Bis zur Bestätigung gilt die bisherige Adresse.", ". Until it is confirmed, the previous address applies.")}</div>}
      <FormErrors errors={{}} error={busy ? null : error} />
      <div className="row"><button type="submit" disabled={!!busy}>{busy === "adresse" ? t("Wird gesendet …", "Sending …") : t("Adresse ändern", "Change address")}</button></div>
    </form>
  );
}

/** Konto löschen lassen: der Zugang endet sofort, das Anmeldekonto löscht der Plattform-Betrieb; Daten der Agentur bleiben. */
function KontoLoeschen({ creator = false }: { creator?: boolean }) {
  const navigate = useNavigate();
  const { busy, error, run } = useAction();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  return (
    <section className="panel stack" aria-labelledby="loeschen-title">
      <h3 id="loeschen-title">{creator ? t("Konto löschen", "Delete account") : t("Meine Anmeldung löschen", "Delete my sign-in")}</h3>
      {/* Studio (Creator-Konto): eine Person ist zugleich Inhaberin; gelöscht wird das ganze Konto unter Abo & Tarif */}
      {creator ? (
        <p>{t("Dein Konto samt allen Daten löschst du unter ", "You delete your account with all data under ")}<Link to="/agentur/einstellungen/abo#konto-loeschen">{t("Abo & Tarif · Kündigung und Daten", "Subscription & plan · Termination and data")}</Link>{t(". Vorher exportierst du dort alles, was du behalten möchtest.", ". Before that you export everything there that you want to keep.")}</p>
      ) : <>
      <p><strong>{t("Löscht nur deinen persönlichen Zugang. Die Agentur und ihre Daten bleiben.", "Deletes only your personal access. The agency and its data remain.")}</strong></p>
      <p>
        {t("Dein Zugang zu allen Agenturen endet sofort; das Anmeldekonto löscht der Plattform-Betrieb danach endgültig. Was du in den Agenturen angelegt hast (Kooperationen, Rechnungen, Protokoll), gehört der Agentur und bleibt dort. Bist du der einzige Admin einer Agentur, übertrage die Rolle vorher oder kündige die Agentur. Ist sie schon gekündigt oder gesperrt, bleibst du für ihre aufbewahrten Belege zuständig: Beantrage dann unter Einstellungen · Abo & Tarif die vorzeitige Löschung oder bitte den Plattform-Betrieb, eine andere Person als Admin einzusetzen.", "Your access to all agencies ends immediately; platform operations then permanently delete the sign-in account. What you created in the agencies (deals, invoices, log) belongs to the agency and stays there. If you are the only admin of an agency, transfer the role first or terminate the agency. If it is already cancelled or suspended, you remain responsible for its retained records: request early deletion under Settings · Plan or ask platform operations to appoint another person as admin.")}</p>
      <p>{t("Die ganze Agentur mit allen Daten löschen (nur Admin): ", "Delete the whole agency with all data (admin only): ")}<Link to="/agentur/einstellungen/abo#konto-loeschen">{t("Abo & Tarif · Kündigung und Daten", "Subscription & plan · Termination and data")}</Link>.</p>
      <div className="row"><button type="button" className="danger" onClick={() => setOpen(true)}>{t("Meine Anmeldung löschen", "Delete my sign-in")}</button></div>
      </>}
      <Confirm open={open} title={t("Meine Anmeldung löschen?", "Delete my sign-in?")} danger busy={busy === "loeschen"} confirmLabel={t("Meine Anmeldung löschen", "Delete my sign-in")} disabled={!confirmed}
        onClose={() => setOpen(false)}
        onConfirm={() => void run("loeschen", async () => {
          await requestAccountDeletion(api, reason.trim() || null);
          await api.signOut({ everywhere: true });
          navigate("/anmelden", { replace: true });
        }, t("Löschung beantragt. Du bist abgemeldet.", "Deletion requested. You have been signed out."))}
        text={<>{t("Dein persönlicher Zugang endet sofort und lässt sich nicht selbst wiederherstellen. Die Agentur und ihre Daten bleiben.", "Your personal access ends immediately and cannot be restored by yourself. The agency and its data remain.")}{error && <span className="tk-error" role="alert"> {error}</span>}</>}
        extra={
          <>
            <label>
              {t("Grund (freiwillig)", "Reason (optional)")}<textarea rows={2} maxLength={400} value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <CheckField label={t("Meine Anmeldung soll gelöscht werden.", "My sign-in should be deleted.")} checked={confirmed} onChange={setConfirmed} />
          </>
        } />
    </section>
  );
}

/**
 * Agenturwechsel: Standorte eines Enterprise-Verbunds (0174) stehen unter dessen Namen zusammen, alle übrigen Agenturen darunter.
 * Ohne lesbare Verbund-Angaben (oder ohne Verbund) bleibt es die einfache Liste.
 */
function AgenturWechsel({ value, orgs, onChange }: { value: string; orgs: { key: string; label: string }[]; onChange: (v: string) => void }) {
  const ids = orgs.map((o) => o.key);
  const gruppen = useQuery({
    queryKey: ["org_groups", "wechsel", ids.join(",")],
    queryFn: async () => {
      const rows = await api.select<{ id: string; group_id: string | null }>("organizations", { columns: "id, group_id", filters: [["id", "in", ids]] });
      const gids = [...new Set(rows.map((r) => r.group_id).filter((g): g is string => !!g))];
      if (!gids.length) return null;
      const namen = await api.select<{ id: string; name: string }>("org_groups", { columns: "id, name", filters: [["id", "in", gids]] });
      return { von: Object.fromEntries(rows.map((r) => [r.id, r.group_id])), namen: Object.fromEntries(namen.map((n) => [n.id, n.name])) };
    },
    staleTime: 5 * 60_000,
  });
  const g = gruppen.data ?? null;
  const verbuende = g ? Object.entries(g.namen).sort((a, b) => a[1].localeCompare(b[1])) : [];
  const einzeln = g ? orgs.filter((o) => !g.von[o.key] || !g.namen[g.von[o.key]!]) : orgs;
  const hint = t("Beim Wechsel werden alle geladenen Daten der bisherigen Agentur verworfen.", "When switching, all loaded data of the previous agency is discarded.");
  if (!verbuende.length) return <SelectField label={t("Agentur wechseln", "Switch agency")} value={value} onChange={onChange} options={orgs} hint={hint} />;
  return (
    <label>
      {t("Agentur wechseln", "Switch agency")}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-describedby="agentur-wechsel-hinweis">
        {verbuende.map(([gid, name]) => (
          <optgroup key={gid} label={t(`Verbund ${name}`, `Group ${name}`)}>
            {orgs.filter((o) => g!.von[o.key] === gid).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </optgroup>
        ))}
        {einzeln.length > 0 && (
          <optgroup label={t("Weitere Agenturen", "Other agencies")}>
            {einzeln.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </optgroup>
        )}
      </select>
      <small id="agentur-wechsel-hinweis">{hint}</small>
    </label>
  );
}
